/**
 * The "change shipping option" resolution path: the shipment details are fine, the carrier or the
 * service is not, so the merchant moves the RMA onto another return method and approves on it.
 * A method a carrier quotes is approved on a rate of that method; one no carrier quotes is approved
 * on its own settings. Nothing on the label's addresses or parcel is touched here - the new method
 * is quoted on the case's own defaults. Owned by ResolveLabelErrorActionStore.
 */
import { makeAutoObservable, runInAction } from "mobx";
import RmaService from "../../../../../services/api/rma-service";
import { rootContainer, TYPE } from "../../../../../services/container/root";
import {
    IChangeShippingOptionApproveRequest,
    IChangeShippingOptionQuotedApproveRequest,
    IChangeShippingOptionUnquotedApproveRequest,
    IShippingDetailsReturnMethod,
} from "../../rma-types";
import RmaPageStore from "../rma-page-store";
import ShippingRatesStore from "./shipping-rates-store";

class ChangeShippingOptionStore {
    private _selectedReturnMethod: IShippingDetailsReturnMethod | null = null;
    /** What the chosen method quotes; replaced wholesale every time the method changes. */
    private _shippingRates = new ShippingRatesStore();
    private _isLoadingRates = false;
    private _isApproving = false;
    /**
     * Counts the quotes asked for. Switching methods leaves the previous request in flight, and a
     * slow answer about the method the merchant has left must not land on the one they are on.
     */
    private _lastRatesRequestId = 0;

    constructor(private _rma: RmaPageStore) {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    /** The return method the RMA would be moved onto; null until the merchant picks one. */
    public get selectedReturnMethodId(): number | null { return this._selectedReturnMethod?.id ?? null; }

    /**
     * Whether the chosen method is approved on a rate. False before a method is picked, and for a
     * method no carrier quotes, which is approved on its own settings instead.
     */
    public get isQuotedMethod(): boolean { return this._selectedReturnMethod?.supports_shipping_rates ?? false; }

    /** What the chosen return method quoted, and which of those rates the merchant picked. */
    public get shippingRates(): ShippingRatesStore { return this._shippingRates; }

    /** True while the chosen method is being quoted. */
    public get isLoadingRates(): boolean { return this._isLoadingRates; }

    /** True when the chosen method left the merchant with nothing to approve. */
    public get ratesFailed(): boolean { return this._shippingRates.failed; }

    /** True while the approve that regenerates the label is in flight. */
    public get isApproving(): boolean { return this._isApproving; }

    /**
     * Whether there is something to approve: a quoted method once its rate is picked, a method no
     * carrier quotes as soon as it is picked.
     */
    public get canApprove(): boolean {
        if (!this._selectedReturnMethod) {
            return false;
        }

        return !this.isQuotedMethod || (!!this._shippingRates.selectedRate && !this._isLoadingRates);
    }

    /**
     * Every method is quoted on its own: the rates of the previous one say nothing about it. No
     * method is chosen for the merchant - they came here to leave the one the RMA is on, and
     * quoting it for them would spend a carrier call on the option they are walking away from.
     *
     * Quoting is what moves the RMA: the quote arrives on an RMA that is already on the method,
     * and it stays there if the carrier prices nothing or if the merchant closes the modal. A
     * method no carrier quotes is not asked for rates at all - the backend would move the RMA and
     * then fail - so it moves only when it is approved. Picking it still drops a quote left in
     * flight for the previous method, which would otherwise fill in rates for a method nobody is on.
     */
    public async selectReturnMethod(returnMethod: IShippingDetailsReturnMethod): Promise<void> {
        this._selectedReturnMethod = returnMethod;

        const requestId = ++this._lastRatesRequestId;
        this._shippingRates.reset();

        if (!returnMethod.supports_shipping_rates) {
            this._isLoadingRates = false;
            return;
        }

        this._isLoadingRates = true;

        try {
            const rates = await this._service.changeReturnMethodAndFetchShippingRates(
                this._rma.rmaId,
                returnMethod.id,
            );
            runInAction(() => {
                if (!this._isStale(requestId)) { this._shippingRates.apply(rates ?? []); }
            });
        } catch (e) {
            console.error("Failed to fetch shipping rates for the chosen return method", e);
            runInAction(() => {
                if (!this._isStale(requestId)) { this._shippingRates.fail(); }
            });
        } finally {
            runInAction(() => {
                if (!this._isStale(requestId)) { this._isLoadingRates = false; }
            });
        }
    }

    /**
     * Approves the RMA onto the chosen method - on the chosen rate, or on the method's own settings
     * when no carrier quotes it - which regenerates the label on the new shipping option. Throws on
     * failure: the caller owns what the merchant is told.
     */
    public async approve(): Promise<void> {
        const request = this._approveRequest();

        this._isApproving = true;

        try {
            await this._service.approve(this._rma.rmaId, request, { silent: true });
        } finally {
            runInAction(() => { this._isApproving = false; });
        }
    }

    /** Drops what the previous RMA was left on, including any quote still in flight for it. */
    public reset(): void {
        this._selectedReturnMethod = null;
        this._shippingRates.reset();
        this._isLoadingRates = false;
        this._lastRatesRequestId += 1;
    }

    private get _service(): RmaService { return rootContainer.get<RmaService>(TYPE.RmaService); }

    /**
     * The body of the approve. Only the method and, for a quoted one, its rate: the label is made
     * from the method's own defaults, so none of the four correction sections go - and a partial set
     * of them would be refused.
     */
    private _approveRequest(): IChangeShippingOptionApproveRequest {
        const returnMethod = this._selectedReturnMethod;

        if (!returnMethod) {
            throw new Error("Approve was asked for before a return method was chosen");
        }

        /** The customer heard about this return already; fixing our label is not their news. */
        const sendEmailNotification = false;

        if (!returnMethod.supports_shipping_rates) {
            return {
                new_return_method_id: returnMethod.id,
                send_email_notification: sendEmailNotification,
            } satisfies IChangeShippingOptionUnquotedApproveRequest;
        }

        const selectedRate = this._shippingRates.selectedRate;

        if (!selectedRate) {
            throw new Error("Approve was asked for before a rate of the quoted return method was chosen");
        }

        return {
            new_return_method_id: returnMethod.id,
            selected_rate_id: selectedRate.id,
            send_email_notification: sendEmailNotification,
        } satisfies IChangeShippingOptionQuotedApproveRequest;
    }

    /** A quote that was superseded while in flight answers about a method nobody is on any more. */
    private _isStale(requestId: number): boolean {
        return requestId !== this._lastRatesRequestId;
    }
}

export default ChangeShippingOptionStore;
